import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import PerfectScrollbar from "react-perfect-scrollbar";
import { Link, useLocation } from "react-router-dom";

import { makeStyles, withStyles, useTheme } from "@material-ui/core/styles";
import Avatar from "@material-ui/core/Avatar";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import Menu from "@material-ui/core/Menu";
import Typography from "@material-ui/core/Typography";
import Badge from "@material-ui/core/Badge";
import Tooltip from "@material-ui/core/Tooltip";

import Notifications from "@material-ui/icons/Notifications";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";

import componentStyles from "assets/theme/components/dropdowns/notifications-dropdown.js";

import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import UtilsFncs from "utils/UtilsFncs";
import Api from "lib/api/httpRequestHelper";
import DialogNotificationsCancelAndXML from "components/Dialogs/DialogNotificationsCancelAndXML";
import { getColorByEnv } from "utils/get-colors-by-env";
import { IsMobile } from "utils/is-mobile";

const useStyles = makeStyles(componentStyles);
const useCustomStyles = makeStyles({
  notificationsIcon: {
    width: 30,
    height: 30,
  },
  paperExtraClass: (isMobile) => ({
    maxWidth: isMobile ? "90%" : 450,
    minWidth: isMobile ? "90%" : 420,
    paddingRight: isMobile ? "1rem" : 0,
    overflow: "hidden",
  }),
  itemsList: {
    marginBottom: "0.5rem",
  },
});

const NotificationIconBadge = withStyles((theme) => ({
  badge: {
    top: 5,
    border: `1px solid ${getColorByEnv({ inverted: true })}`,
    backgroundColor: getColorByEnv({ inverted: true }),
    color: getColorByEnv(),
    display: "flex",
    height: 20,
    width: 20,
    borderRadius: "50%",
  },
}))(Badge);

const wasRead = (toCheck) => {
  return toCheck.DataLeitura;
};

export default function NotificationsDropdown({
  userNotifications,
  userAllNotifications,
}) {
  const today = new Date().toDateString();
  const verifyDateOpenDialog = localStorage.getItem("dateNotification");

  const isMobile = IsMobile();
  const classes = { ...useStyles(), ...useCustomStyles(isMobile) };
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [anchorMargin, setAnchorMargin] = useState(null);
  const [items, setItems] = useState(userNotifications);
  const [markingAsRead, setMarkingAsRead] = useState(false);
  const [hasSetaNotification, setHasSetaNotification] = useState(false);
  const [hasNotificationCancel, setHasNotificationCancel] = useState(false);
  const [hasNotificationPending, setHasNotificationPending] = useState(false);
  const [hasNotificationExchanges, setHasNotificationExchanges] =
    useState(false);
  const [listOrders, setListOrders] = useState([]);
  const snackbar = useSnackbar();
  const location = useLocation();
  const notificationsListRef = useRef();
  const unreadItems = useMemo(
    () => items.filter((item) => !item.DataLeitura),
    [items]
  );
  const listRef = useRef(null);

  const isMenuOpen = Boolean(anchorEl);

  const handleProfileMenuOpen = useCallback((event) => {
    setAnchorMargin(null);

    setAnchorMargin(event.currentTarget.clientHeight);
    setAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const markNotificationAsRead = useCallback(
    async (readNotification) => {
      if (
        !readNotification ||
        (unreadItems.length && !wasRead(readNotification))
      ) {
        setMarkingAsRead(true);

        Api.post(
          "update-notification-to-read",
          "notification",
          {
            idsNotificacoes: !readNotification
              ? unreadItems
                  ?.map((notification) => notification.IdNotificacaoHistorico)
                  .join(",")
              : readNotification.IdNotificacaoHistorico,
          },
          true
        )
          .then((res) => {
            if (res.Success) {
              const updatedItems = items?.map((item) => {
                const updatedItem = res.Result.find(
                  (item2) =>
                    item.IdNotificacaoHistorico === item2.IdNotificacaoHistorico
                );

                return updatedItem || item;
              });

              setItems(updatedItems);
            }
          })
          .catch((error) => {
            snackbar.showMessage(
              "Ocorreu um erro ao ler notificação. Verifique os parâmetros informados.",
              {
                severity: "error",
              }
            );
            console.log(error);
          })
          .finally(() => {
            setMarkingAsRead(false);
          });
      }

      handleMenuClose();
    },
    [handleMenuClose, items, snackbar, unreadItems]
  );

  const handleNotificationRedirect = useCallback(
    (notification) => {
      markNotificationAsRead(notification);
    },
    [markNotificationAsRead]
  );

  const menuId = "dropdowns-notifications-dropdown-id";
  const renderMenu = (
    <Menu
      ref={notificationsListRef}
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      id={menuId}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      open={isMenuOpen}
      onClose={handleMenuClose}
      classes={{
        list: classes.menuList,
        paper: `${classes.menuPaper}  ${classes.paperExtraClass}`,
      }}
      style={{ marginTop: anchorMargin }}
    >
      <Box padding="1rem" borderBottom={"1px solid #e9ecef"}>
        <Typography
          variant="h6"
          component="h6"
          className={classes.typographyH6}
        >
          {unreadItems?.length ? (
            <>
              Você tem{" "}
              <Box component="strong" color={theme.palette.info.main}>
                {unreadItems.length}
              </Box>{" "}
              novas notificações.
            </>
          ) : (
            "Você não tem novas notificações"
          )}
        </Typography>
      </Box>
      <PerfectScrollbar
        options={{ suppressScrollX: true, useBothWheelAxes: false }}
        className={classes.itemsList}
        style={isMobile ? { maxHeight: 300 } : { maxHeight: 435 }}
      >
        <List ref={listRef} style={{ marginRight: "1rem", padding: 0 }}>
          {items
            ? items?.map((prop, key) => {
                const link =
                  prop.LinkDestinoNotificacao?.replace(
                    "https://karsten.digital/admin/",
                    ""
                  ) || "#";

                return (
                  <ListItem
                    key={key}
                    className={classes.listItemRoot}
                    style={{
                      maxHeight: 150,
                    }}
                    button
                    component={Link}
                    to={
                      location.pathname !== "/home"
                        ? link
                        : link !== "#"
                        ? "/admin/" + link
                        : link
                    }
                    onClick={() => handleNotificationRedirect(prop)}
                  >
                    <Grid container alignItems="center">
                      <Box
                        flex="0 0 auto"
                        width="15%"
                        maxWidth="100%"
                        position="relative"
                        paddingRight="15px"
                        paddingLeft="15px"
                        minHeight="1px"
                        display="flex"
                      >
                        <Avatar
                          alt="..."
                          src={prop.UrlIcone}
                          classes={{
                            root: classes.avatarRoot,
                          }}
                          style={{
                            width: isMobile ? 36 : 48,
                            height: isMobile ? 36 : 48,
                          }}
                        />
                      </Box>
                      <Box
                        flexBasis="0"
                        flexGrow="1"
                        width="85%"
                        position="relative"
                        paddingRight="15px"
                        paddingLeft="15px"
                        minHeight="1px"
                      >
                        <Box display="flex" justifyContent="space-between">
                          <Tooltip title={prop.Titulo}>
                            <Typography
                              variant="h4"
                              component="h4"
                              className={classes.typographyH4}
                              style={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {prop.Titulo}
                            </Typography>
                          </Tooltip>
                          <Box
                            component="span"
                            fontSize=".9rem"
                            fontWeight="300"
                            lineHeight="1.7"
                            margin="0"
                            display="flex"
                            alignItems="center"
                            width="10%"
                          >
                            {!wasRead(prop) ? (
                              <FiberManualRecordIcon
                                color="primary"
                                style={{ marginLeft: "1rem" }}
                              />
                            ) : null}
                          </Box>
                        </Box>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="flex-end"
                        >
                          <Tooltip title={prop.Conteudo}>
                            <Box
                              component="p"
                              fontSize=".875rem"
                              fontWeight="300"
                              lineHeight="1.7"
                              margin="0"
                              style={{
                                display: "-webkit-box",
                                maxWidth: "75%",
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                WebkitLineClamp: 2,
                              }}
                            >
                              {prop.Conteudo}
                            </Box>
                          </Tooltip>
                          <Box
                            textAlign="right"
                            color={theme.palette.gray[600]}
                            component="p"
                            fontSize=".800rem"
                            fontWeight="300"
                            lineHeight="1.7"
                            margin="0"
                          >
                            {UtilsFncs.GetTimeAgo(prop.DataDisparoNotificacao)}
                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                  </ListItem>
                );
              })
            : null}
        </List>
      </PerfectScrollbar>
      {unreadItems.length > 0 && (
        <Box
          component="span"
          padding={isMobile ? "0" : ".5rem 1rem"}
          paddingBottom={isMobile ? "1.5rem" : "2.5rem"}
          fontSize=".875rem"
          color={theme.palette.info.main}
          fontWeight="600"
          textAlign="center"
          paddingTop="1rem"
          display="block"
          className={classes.boxAnchor}
          disabled={markingAsRead}
          onClick={() => markNotificationAsRead()}
          style={{ cursor: "pointer" }}
        >
          Marcar todas como lidas
        </Box>
      )}
    </Menu>
  );

  const handleGetNotifications = () => {
    setOpenDialog(false);
    setHasSetaNotification(false);
    setHasNotificationCancel(false);
    setHasNotificationPending(false);
    setHasNotificationExchanges(false);

    userAllNotifications?.map((newNotification) => {
      if (newNotification?.IdNotificacaoTopico === 1) {
        setHasNotificationPending(true);
        setOpenDialog(true);
      }
      if (newNotification?.IdNotificacaoTopico === 4) {
        setHasNotificationCancel(true);
        setOpenDialog(true);
      }
      if (newNotification?.IdNotificacaoTopico === 9) {
        setHasNotificationExchanges(true);
        setOpenDialog(true);
      }
      if (newNotification?.IdNotificacaoTopico === 8) {
        const list = [];

        setHasSetaNotification(true);
        const regex = /(Pedido)(.*)(não)/;

        if (newNotification?.Conteudo?.includes("não integrado")) {
          const text = newNotification?.Conteudo?.match(regex)[2];
          if (!list.includes(text)) {
            list.push(text);
          }
          setListOrders(list);
        }
      }
    });
  };

  useEffect(() => {
    if (hasSetaNotification && listOrders?.length !== 0) {
      const orders = listOrders?.map((item) => item).join(",");
      snackbar.showMessage(
        `Erro ao tentar integrar a venda prateleira infinita do(s) pedido(s)
        ${orders}`,
        {
          severity: "error",
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSetaNotification, listOrders]);

  useEffect(() => {
    setHasNotificationCancel(false);
    setHasNotificationPending(false);
    setHasNotificationExchanges(false);

    if (!UtilsFncs.ArrayEquals(userNotifications, items)) {
      setItems(userNotifications);
    }
    handleGetNotifications();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userNotifications, userAllNotifications]);

  return (
    <>
      <IconButton
        aria-label="botao de notificacoes"
        aria-controls={menuId}
        aria-haspopup="true"
        onClick={handleProfileMenuOpen}
        classes={{
          label: classes.buttonLabel,
        }}
        disabled={markingAsRead}
      >
        <NotificationIconBadge
          badgeContent={unreadItems.length}
          color="primary"
        >
          <Notifications className={classes.notificationsIcon} />
        </NotificationIconBadge>
      </IconButton>
      {renderMenu}
      {openDialog && today !== verifyDateOpenDialog && (
        <DialogNotificationsCancelAndXML
          hasNotificationCancel={hasNotificationCancel}
          hasNotificationPending={hasNotificationPending}
          hasNotificationExchanges={hasNotificationExchanges}
          onClose={() => {
            setOpenDialog(false);
            localStorage.setItem("dateNotification", new Date().toDateString());
          }}
        />
      )}
    </>
  );
}
